import { createApi } from "@reduxjs/toolkit/query/react";

import { BaseResponse } from "../../../shared/types";

import { baseApiQuery } from "../../../shared/api";

import {
  AboutRequest,
  AvatarResponse,
  Company,
  CompetenciesRequest,
  CreateCompanyRequest,
  GetCompaniesResponse,
  GetInviteesParams,
  GetInviteesResponse,
  HobbiesRequest,
  Permissions,
  SaveAsResidentRequest,
  SaveAsUserRequest,
  SocialsRequest,
  UpdateCompanyRequest,
  User,
} from "./types";

export const meApi = createApi({
  reducerPath: "meApi",
  baseQuery: baseApiQuery,
  tagTypes: ["Profile", "Companies", "Company"],
  endpoints: (builder) => ({
    getMyProfile: builder.query<User, void>({
      query: () => "/my",
      transformResponse: (response: unknown): User => {
        if (!response || typeof response !== "object") {
          throw new Error("Invalid response format");
        }
        return response as User;
      },
      providesTags: ["Profile"],
    }),

    saveAsUser: builder.mutation<BaseResponse, SaveAsUserRequest>({
      query: (body) => ({
        url: "/my/save-as-user",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    saveAsResident: builder.mutation<BaseResponse, SaveAsResidentRequest>({
      query: (body) => ({
        url: "/my/save-as-resident",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    updateHobbies: builder.mutation<BaseResponse, HobbiesRequest>({
      query: (body) => ({
        url: "/my/hobbies",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    updateCompetencies: builder.mutation<BaseResponse, CompetenciesRequest>({
      query: (body) => ({
        url: "/my/competencies",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    updateAbout: builder.mutation<BaseResponse, AboutRequest>({
      query: (body) => ({
        url: "/my/about",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    updateSocials: builder.mutation<BaseResponse, SocialsRequest>({
      query: (body) => ({
        url: "/my/socials",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    addCompany: builder.mutation<Company, CreateCompanyRequest>({
      query: (body) => ({
        url: "/my/companies",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Profile", "Companies"],
    }),

    updateCompany: builder.mutation<Company, UpdateCompanyRequest>({
      query: (body) => ({
        url: "/my/companies",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile", "Companies", "Company"],
    }),

    getCompanies: builder.query<GetCompaniesResponse, void>({
      query: () => "/my/companies",
      providesTags: ["Companies"],
    }),

    getCompanyById: builder.query<Company, number>({
      query: (id) => `/my/companies/${id}`,
      providesTags: ["Companies"],
    }),

    uploadAvatar: builder.mutation<AvatarResponse, FormData>({
      query: (body) => ({
        url: "/my/avatar",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    removeAvatar: builder.mutation<BaseResponse, void>({
      query: () => ({
        url: "/my/avatar",
        method: "DELETE",
      }),
    }),

    uploadCompanyAvatar: builder.mutation<
      AvatarResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/my/companies/${id}/avatar`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["Companies", "Company"],
    }),

    removeCompanyAvatar: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/my/companies/${id}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: ["Companies", "Company"],
    }),

    getInvitedResidents: builder.query<GetInviteesResponse, GetInviteesParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();

        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.cursor) queryParams.append("cursor", params.cursor);
        if (params.limit) queryParams.append("limit", params.limit.toString());

        return `/my/invitees${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["Profile"],
    }),

    getMyPermissions: builder.query<Permissions, void>({
      query: () => "/my/permissions",
      providesTags: ["Profile"],
    }),
  }),
});

export const {
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  useSaveAsUserMutation,
  useSaveAsResidentMutation,
  useUpdateHobbiesMutation,
  useUpdateCompetenciesMutation,
  useUpdateAboutMutation,
  useUpdateSocialsMutation,
  useAddCompanyMutation,
  useUpdateCompanyMutation,
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useLazyGetCompanyByIdQuery,
  useUploadAvatarMutation,
  useUploadCompanyAvatarMutation,
  useRemoveCompanyAvatarMutation,
  useGetInvitedResidentsQuery,
  useGetMyPermissionsQuery,
  useLazyGetMyPermissionsQuery,
} = meApi;
